import axios from "axios";

// Sessions are httpOnly cookies issued by the backend's Better Auth instance,
// so `withCredentials` is what authenticates every request — there is no token
// to attach and nothing readable from JavaScript.
//
// The Authorization header and the silent /refresh-token retry that used to
// live here are gone with the old JWT stack: Better Auth slides the session
// expiry forward on activity, so there is nothing to refresh.
const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Pages that must not bounce to /login on a 401 — they either ARE the signed-
// out experience or are reached from an emailed link.
const PUBLIC_PATHS = ["/login", "/password", "/forgot-password", "/pdf"];

const isOnPublicPath = () =>
  PUBLIC_PATHS.some((path) => window.location.pathname.startsWith(path));

api.interceptors.request.use(
  (config) => {
    // S3 presigned URLs carry their own signature; an extra credentialed
    // request would be rejected by the bucket.
    if (config.url?.includes("amazonaws.com")) {
      delete config.headers["Authorization"];
      config.withCredentials = false;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const code = error.response?.data?.code;

    // Signed in, but hasn't finished two-factor enrolment. Every guarded route
    // answers this until they do, so send them to the one page that can fix it
    // rather than to /login, which would look like a broken password.
    if (status === 403 && code === "MFA_REQUIRED") {
      if (!window.location.pathname.startsWith("/mfa-setup")) {
        window.location.href = "/mfa-setup";
      }
      return Promise.reject(error);
    }

    // No session, or one that has been revoked underneath us.
    if (status === 401 || code === "ACCOUNT_INACTIVE") {
      if (!isOnPublicPath()) {
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  },
);

export default api;
